import { OnboardingStep } from "@clockwise/web-commons/src/util/onboarding.util";
import { OnboardingActions, IOnboardingAction } from "@actions/onboarding.actions";
import { FastCrawlFlowStates, FatalFastCrawlFlowStates } from "@state/onboarding";

import { OnboardingMeta, OnboardingType } from "@components/onboarding-nux/OnboardingNUX.d";
import { onboardingMeta } from "@state/local-storage";

export interface IOnboardingState {
  onboardingLoaded: boolean;
  onboardingFlowStateCurrentStatus: OnboardingStep;
  onboardingFinished: boolean;
  onboardingFocusTimeSyncSaved: boolean;

  fastCrawlFlowStateCurrentStatus: FastCrawlFlowStates;
  fastCrawlFlowStateErrorMessage: string | null;
  fastCrawlFinished: boolean;
  fastCrawlUserCrawledComplete: boolean;
  fastCrawlTeammatesIdentifiedComplete: boolean;
  fastCrawlRunning: boolean;
  fastCrawlFatalState: boolean;
  fastCrawlUserCrawledRefetchComplete: boolean;
  fastCrawlTeammatesIdentifiedRefetchComplete: boolean;
  fastCrawlFinishedRefetchComplete: boolean;

  partnerLogo: "asana" | "";
  onboardingMeta: OnboardingMeta;
}

export const initialOnboardingState: IOnboardingState = {
  onboardingLoaded: false,
  onboardingFlowStateCurrentStatus: OnboardingStep.Finished,
  onboardingFinished: true,
  onboardingFocusTimeSyncSaved: true,

  fastCrawlFlowStateCurrentStatus: FastCrawlFlowStates.Finished,
  fastCrawlFlowStateErrorMessage: null,
  fastCrawlFinished: true,
  fastCrawlUserCrawledComplete: true,
  fastCrawlTeammatesIdentifiedComplete: true,
  fastCrawlRunning: false,
  fastCrawlFatalState: false,
  fastCrawlUserCrawledRefetchComplete: true,
  fastCrawlTeammatesIdentifiedRefetchComplete: true,
  fastCrawlFinishedRefetchComplete: true,

  partnerLogo: "",
  onboardingMeta: {
    onboardingType: onboardingMeta.get("onboardingType") || OnboardingType.Default,
    onboardingTypeSource: onboardingMeta.get("onboardingTypeSource") || null,
  },
};

export function onboardingReducer(state = initialOnboardingState, action: IOnboardingAction) {
  switch (action.type) {
    case OnboardingActions.SetOnboardingLoaded:
      return {
        ...state,
        onboardingLoaded: (action.data && action.data.onboardingLoaded) || false,
      };
    case OnboardingActions.SetOnboardingFlowState:
      // sanity
      if (!action.data || !action.data.onboardingFlowState) {
        return state;
      }

      // setup
      const onboardingFlowState = action.data.onboardingFlowState;
      const onboardingFlowStateCurrentStatus = onboardingFlowState.current.state;

      // statuses
      let onboardingFinished = true;
      let onboardingFocusTimeSyncSaved = true;
      switch (onboardingFlowStateCurrentStatus) {
        case OnboardingStep.Unstarted:
        case OnboardingStep.Survey:
        case OnboardingStep.IndividualJtbdSurvey:
        case OnboardingStep.TeamJoinCreate:
        case OnboardingStep.WorkingHours:
        case OnboardingStep.Lunch:
        case OnboardingStep.FocusTimeSyncAndLunchHolds:
        case OnboardingStep.Analyzing:
        case OnboardingStep.IntroduceFocusTime:
        case OnboardingStep.AutopilotIntroduction:
        case OnboardingStep.CarouselIntroduction:
        case OnboardingStep.AutopilotCarousel:
        case OnboardingStep.Autopilot:
        case OnboardingStep.FlexMeeting:
        case OnboardingStep.ScheduleTestEvent:
        case OnboardingStep.Links:
        case OnboardingStep.FocusTimeSync:
          onboardingFinished = false;
          onboardingFocusTimeSyncSaved = false;
          break;
        case OnboardingStep.SegmentedSummary:
        case OnboardingStep.Invite:
        case OnboardingStep.ColorCoding:
        case OnboardingStep.PrivateCalendarSync:
        case OnboardingStep.TravelTime:
        case OnboardingStep.Slack:
        case OnboardingStep.Summary:
          onboardingFinished = false;
          break;
        case OnboardingStep.Finished:
        default:
          break;
      }
      return {
        ...state,
        onboardingFlowStateCurrentStatus,
        onboardingFocusTimeSyncSaved,
        onboardingFinished,
      };
    case OnboardingActions.SetOnboardingFocusTimeSyncSaved:
      return {
        ...state,
        onboardingFocusTimeSyncSaved: true,
      };
    case OnboardingActions.SetFastCrawlUserCrawledRefetchComplete:
      return {
        ...state,
        fastCrawlUserCrawledRefetchComplete: true,
      };
    case OnboardingActions.SetFastCrawlTeammatesIdentifiedRefetchComplete:
      return {
        ...state,
        fastCrawlTeammatesIdentifiedRefetchComplete: true,
        fastCrawlUserCrawledRefetchComplete: true,
      };
    case OnboardingActions.SetFastCrawlFinishedRefetchComplete:
      return {
        ...state,
        fastCrawlFinishedRefetchComplete: true,
        fastCrawlTeammatesIdentifiedRefetchComplete: true,
        fastCrawlUserCrawledRefetchComplete: true,
      };
    case OnboardingActions.SetFastCrawlFlowState:
      // sanity
      if (!action.data || !action.data.fastCrawlFlowState) {
        return state;
      }

      // setup
      const fastCrawlFlowState = action.data.fastCrawlFlowState;
      const fastCrawlFlowStateCurrentStatus = fastCrawlFlowState.current.state;
      const fastCrawlFlowStateErrorMessage = fastCrawlFlowState.current.errorMessage;

      // for statuses which need refreshes, we care if we just changed to it
      // we special case changing from Finished since that is the initial state
      // and we assume its the first time loading data
      const statusChanged =
        state.fastCrawlFlowStateCurrentStatus !== fastCrawlFlowStateCurrentStatus &&
        state.fastCrawlFlowStateCurrentStatus !== "Finished";

      // statuses
      let fastCrawlUserCrawledComplete = true;
      let fastCrawlUserCrawledRefetchComplete = true;
      let fastCrawlTeammatesIdentifiedComplete = true;
      let fastCrawlTeammatesIdentifiedRefetchComplete = true;
      let fastCrawlFinished = true;
      let fastCrawlFinishedRefetchComplete = true;
      switch (fastCrawlFlowStateCurrentStatus) {
        case "Unstarted":
        case "CredentialsVerified":
          fastCrawlUserCrawledComplete = false;
          fastCrawlUserCrawledRefetchComplete = false;
          fastCrawlTeammatesIdentifiedComplete = false;
          fastCrawlTeammatesIdentifiedRefetchComplete = false;
          fastCrawlFinished = false;
          fastCrawlFinishedRefetchComplete = false;
          break;
        case "UserCrawled":
          if (statusChanged) {
            fastCrawlUserCrawledRefetchComplete = false;
          }
          fastCrawlTeammatesIdentifiedComplete = false;
          fastCrawlTeammatesIdentifiedRefetchComplete = false;
          fastCrawlFinished = false;
          fastCrawlFinishedRefetchComplete = false;
          break;
        case "TeammatesIdentified":
          if (statusChanged) {
            fastCrawlTeammatesIdentifiedRefetchComplete = false;
          }
          fastCrawlFinished = false;
          fastCrawlFinishedRefetchComplete = false;
          break;
        case "InvitesSuggested":
        case "TeammatesEventsCrawled":
        case "CalendarAliasesResolved":
        case "TeammatesEventsSaved":
          fastCrawlFinished = false;
          fastCrawlFinishedRefetchComplete = false;
          break;
        case "Finished":
          if (statusChanged) {
            fastCrawlFinishedRefetchComplete = false;
          }
          break;
        default:
          break;
      }

      // running and errors
      const fastCrawlRunning = !fastCrawlFinished;
      const fastCrawlFatalState =
        !!fastCrawlFlowState.current.errorMessage &&
        (FatalFastCrawlFlowStates.indexOf(fastCrawlFlowState.previousState) !== -1 ||
          !fastCrawlFlowState.previousState);

      // finish up
      const newState = {
        ...state,
        fastCrawlFlowStateCurrentStatus,
        fastCrawlFlowStateErrorMessage,
        fastCrawlUserCrawledComplete,
        fastCrawlUserCrawledRefetchComplete,
        fastCrawlTeammatesIdentifiedComplete,
        fastCrawlTeammatesIdentifiedRefetchComplete,
        fastCrawlFinished,
        fastCrawlFinishedRefetchComplete,
        fastCrawlFatalState,
        fastCrawlRunning,
      };
      return newState;
    case OnboardingActions.SetPartnerLogo:
      return {
        ...state,
        partnerLogo: action?.data?.partnerLogo ?? "",
      };
    case OnboardingActions.SetOnboardingMeta:
      return {
        ...state,
        onboardingMeta: {
          onboardingType: action.data?.onboardingMeta?.onboardingType ?? OnboardingType.Default,
          onboardingTypeSource: action.data?.onboardingMeta?.onboardingTypeSource ?? null,
        },
      };
    default:
      return state;
  }
}
